/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import { confirm } from 'components/molecules/Confirmation'
import * as generalHelper from 'helpers/generalHelper'
import { getAuthorizationExpirationRowCss } from 'helpers/orderRowHighlight'
import { useOrdersTable } from 'hooks/useOrdersTable'
import moment from 'moment'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useState, useMemo, useEffect } from 'react'
import Button from 'react-bootstrap/Button'

import {
  useAddToBillingQueueMutation,
  useGetLabOrdersQuery,
  useRemoveFromBillingQueueMutation,
  useSubmitBillingMutation,
  useGetRecentLabOrdersQuery,
  useCreateChargesMutation,
  useMarkOrdersAsBilledMutation,
  useRefaxOrderReportMutation,
} from 'store/services/labOrder'
import { useGetLabPhysiciansQuery } from 'store/services/labPhysician'
import { useGetLabOfficesQuery, useGetCompaniesQuery } from 'store/services/office'
import { useDebounce } from 'use-debounce'
import { useGetLabPayersQuery } from 'store/services/payer'
import DashboardContent from '../atoms/DashboardContent'
import { COMPOUND_BUCKET_MAP, COMPOUND_BUCKETS } from '../constants/orderStatus'
import routes from '../constants/routes'
import { AdvancedSearch } from './AdvancedSearch'
import { bucketColumnDefaults, getFollowUpFilterConfig, OUTSOURCE_VISIBILITY_BUCKETS } from './columnConfig'
import OrderTableColumns from './OrderTableColumns'
import { LabBucketValue, Physician, SavedBucketDto } from 'store/types'
import { useAppSelector } from 'hooks/redux'
import { normalizeSavedBucketTestType } from '../constants/savedBuckets'
import { MarkBilledDialog } from 'components/organisms/reports/MarkBilledDialog'

const StyledTableWrapper = styled(TableWrapper)`
  thead {
    z-index: 1;
  }
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.2rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`

export type SearchParams = {
  orderId?: string
  companyId?: number
  officeId?: number
  patientFirstName?: string
  patientLastName?: string
  patientDob?: string
  physicianIds?: string
  physicianNpis?: string[]
  primaryPayorId?: string
  physicianName?: string
  payerName?: string
  verified?: boolean
  aobVerified?: boolean
  hasUploads?: boolean
  payerId?: string
  humanFollowUpOnly?: boolean
  outsourceVisibility?: 'visible' | 'hidden' | ''
}

const initialSearch: SearchParams = {
  companyId: undefined,
  officeId: undefined,
  orderId: '',
  patientFirstName: '',
  patientLastName: '',
  patientDob: '',
  physicianIds: '',
  physicianName: '',
  payerId: '',
  verified: false,
  aobVerified: false,
  hasUploads: false,
  outsourceVisibility: '',
}

const Order = () => {
  const currentUserId = useAppSelector((state) => state.init.id)
  const [search, setSearch] = useState<SearchParams>(initialSearch)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [showMarkBilledDialog, setShowMarkBilledDialog] = useState(false)
  const [isRetryingFaxes, setIsRetryingFaxes] = useState(false)
  const [companySearch, setCompanySearch] = useState('')
  const [debouncedCompanySearch] = useDebounce(companySearch, 300)
  const clearSearch = () => {
    setSearch(initialSearch)
    setCompanySearch('')
  }
  const [debouncedSearch] = useDebounce(search, 500)
  const tableHelpers = useOrdersTable(debouncedSearch, clearSearch)

  // Selection must not leak across buckets/test types — bulk buttons would act on
  // rows that are no longer visible.
  useEffect(() => {
    setSelectedIds([])
  }, [tableHelpers.bucket, tableHelpers.testType])

  const bucketFilter = COMPOUND_BUCKETS.includes(tableHelpers.bucket)
    ? COMPOUND_BUCKET_MAP[tableHelpers.bucket]
    : tableHelpers.bucket

  const { data: physicianList, isFetching: physiciansLoading } = useGetLabPhysiciansQuery(
    typeof debouncedSearch?.physicianName === 'string' &&
      debouncedSearch?.physicianName?.length >= 2
      ? {
          name: debouncedSearch?.physicianName,
        }
      : skipToken,
  )
  const physicians = useMemo(() => {
    if (!Array.isArray(physicianList)) {
      return []
    }
    return physicianList
      .filter((physician: Physician): boolean => {
        if (search.officeId) {
          return Number(search.officeId) === Number(physician.officeId)
        }
        return true
      })
      .filter((physician: Physician, index: number, list: Physician[]): boolean => {
        const firstIndex = list.findIndex((p: Physician) => String(p.npi) === String(physician.npi))
        return firstIndex === index
      })
      .sort((a: Physician, b: Physician) => {
        return a.lastName === b.lastName
          ? String(a.firstName).localeCompare(b.firstName)
          : String(a.lastName).localeCompare(b.lastName)
      })
  }, [physicianList, search.officeId])

  const { data: payers } = useGetLabPayersQuery({
    name: debouncedSearch?.payerName,
    active: true,
  })
  const { data: companies } = useGetCompaniesQuery({
    name: debouncedCompanySearch || undefined,
  })
  const { data: offices } = useGetLabOfficesQuery(
    debouncedSearch?.companyId ? { companyId: debouncedSearch.companyId } : {}
  )
  const [addToBillingQueue] = useAddToBillingQueueMutation()
  const [createCharges] = useCreateChargesMutation()
  const [removeFromBillingQueue] = useRemoveFromBillingQueueMutation()
  const [submitBilling] = useSubmitBillingMutation()
  const [markOrdersAsBilled] = useMarkOrdersAsBilledMutation()
  const [refaxOrderReport] = useRefaxOrderReportMutation()
  const patientDobText = debouncedSearch?.patientDob?.replaceAll(' ', '')
  const patientDob = patientDobText?.length === 10 ? moment(patientDobText) : null
  const followUpFilterConfig = getFollowUpFilterConfig(tableHelpers.testType, tableHelpers.bucket)
  const orderFetchFn =
    tableHelpers.bucket === 'RECENT' ? useGetRecentLabOrdersQuery : useGetLabOrdersQuery

  const {
    physicianName,
    companyId,
    officeId,
    humanFollowUpOnly,
    outsourceVisibility,
    ...searchParams
  } = debouncedSearch
  const queryParams = {
    orderId: debouncedSearch.orderId,
    companyIds: debouncedSearch.companyId ? [debouncedSearch.companyId] : undefined,
    officeIds: debouncedSearch.officeId ? [debouncedSearch.officeId] : undefined,
    types: tableHelpers.testType?.replace(/_/g, ',') || 'POX',
    buckets: bucketFilter === 'RECENT' ? undefined : bucketFilter,
    page: tableHelpers.currentPage,
    size: tableHelpers.pageSize,
    sortField: tableHelpers.sortField,
    sortOrder:
      tableHelpers.sortField === 'deviceAssignedAt'
        ? tableHelpers.sortOrder === 'asc'
          ? 'desc'
          : 'asc'
        : tableHelpers?.sortOrder,
    ...generalHelper.cleanObj(searchParams, true),
    ...(debouncedSearch.patientDob && {
      patientDob: patientDob?.isValid() ? patientDob?.format('YYYY-MM-DD') : undefined,
    }),
    ...(humanFollowUpOnly && followUpFilterConfig.showHumanFollowUpFilter && {
      humanFollowUpRequired: true,
      ...(followUpFilterConfig.followUpNamespace && {
        followUpNamespace: followUpFilterConfig.followUpNamespace,
      }),
    }),
    // Only sent on the buckets that offer the filter, so a selection made there never
    // silently filters an unsupported bucket (same pattern as humanFollowUpOnly above).
    ...(outsourceVisibility && OUTSOURCE_VISIBILITY_BUCKETS.includes(tableHelpers.bucket ?? '') && {
      outsourceVisibility,
    }),
  };


  const {
    data: orders,
    isLoading,
    refetch: refetchOrders,
  } = orderFetchFn(queryParams, {
    pollingInterval: 120000,
  });


  const changeSearch = (field: string, value: string | boolean) => {
    setSearch((p) => ({ ...p, [field]: value }))
  }

  // The list endpoint actually returns { items, stats, ... } even though the service types
  // it as Order[]; read stats loosely like the rest of this page does.
  const ordersStats = (orders as { stats?: { [key: string]: { [key: string]: string } } } | undefined)
    ?.stats

  // react-table requires a stable columns identity: built inline, a fresh array on every
  // render forced a full rebuild of all rows/cells (100 rows x ~15 heavy cells) on every
  // keystroke, poll and URL change — a large share of the list-load jank.
  const tableColumns = useMemo(
    () =>
      OrderTableColumns({
        testType: tableHelpers.testType,
        bucket: tableHelpers.bucket,
        setSelectedIds,
        selectedIds,
        stats: ordersStats,
      }),
    [tableHelpers.testType, tableHelpers.bucket, setSelectedIds, selectedIds, ordersStats],
  )

  const normalizedSavedBucketTestType = normalizeSavedBucketTestType(tableHelpers.testType)
  const currentSavedBucket =
    tableHelpers.bucket && tableHelpers.bucket !== 'RECENT' && normalizedSavedBucketTestType
      ? ({
          bucket: tableHelpers.bucket as LabBucketValue,
          testType: normalizedSavedBucketTestType,
        } as SavedBucketDto)
      : null

  return (
    <div>
      <DashboardContent
        title="Orders"
        icon="fas fa-folder-open"
        content={
          <div className="card card-primary mt-1">
            <StyledTableWrapper
              className="card-body row"
              css={{
                overflowY: !orders?.items?.length || orders?.items?.length < 10 ? 'unset' : 'auto',
              }}>
              <AdvancedSearch
                search={search}
                changeSearch={changeSearch}
                clearSearch={clearSearch}
                physiciansLoading={physiciansLoading}
                physicians={physicians}
                payers={payers}
                companies={companies}
                offices={offices}
                tableHelpers={tableHelpers}
                showExport={tableHelpers.bucket === 'INS_VER'}
                onCompanySearch={setCompanySearch}
                currentUserId={currentUserId ? Number(currentUserId) : undefined}
                currentSavedBucket={currentSavedBucket}
                followUpFilterConfig={followUpFilterConfig}
              />
              {(tableHelpers.bucket === 'BILLING_REVIEW' ||
                tableHelpers.bucket === 'BILLING_CHARGE_BLOCKED') && (
                <>
                  <Button
                    variant="primary"
                    disabled={!selectedIds.length}
                    onClick={() => setShowMarkBilledDialog(true)}
                    className="ml-2 mb-4">
                    Mark as Billed
                  </Button>
                  <Button
                    variant="primary"
                    disabled={!selectedIds.length}
                    onClick={async () => {
                      confirm('', {
                        title: 'Are you sure?',
                        description: 'This will request charge creation for the selected orders.',
                        yesAction: 'Continue',
                        noAction: 'Cancel',
                      })
                        .then(async () => {
                          setSelectedIds([])

                          try {
                            await createCharges({ orders: selectedIds }).unwrap()
                            refetchOrders()
                          } catch (e) {
                            console.log(e)
                            if (e?.data?.message) {
                              confirm('', {
                                title: 'Error',
                                description: e?.data?.message,
                                yesAction: 'OK',
                                noAction: undefined,
                              })
                            }
                          }
                        })
                        .catch(() => {})
                    }}
                    className="ml-2 mb-4">
                    Create Charges
                  </Button>
                </>
              )}
              {tableHelpers.bucket === 'BILLING_REVIEW' && (
                <>
                  <Button
                    variant="primary"
                    disabled={!selectedIds.length}
                    onClick={async () => {
                      confirm('', {
                        title: 'Are you sure?',
                        description: 'This will add selected orders to the CureMD billing queue.',
                        yesAction: 'Continue',
                        noAction: 'Cancel',
                      })
                        .then(async () => {
                          setSelectedIds([])

                          try {
                            await addToBillingQueue({ orders: selectedIds }).unwrap()
                            refetchOrders()
                          } catch (e) {
                            console.log(e)
                            if (e?.data?.message) {
                              confirm('', {
                                title: 'Error',
                                description: e?.data?.message,
                                yesAction: 'OK',
                                noAction: undefined,
                              })
                            }
                          }
                        })
                        .catch(() => {})
                    }}
                    className="ml-2 mb-4">
                    Send To CureMD
                  </Button>
                </>
              )}
              {(tableHelpers.bucket === 'BILLING_QUEUED' ||
                tableHelpers.bucket === 'BILLING_FAILED') && (
                <div className="d-flex mb-3 mt-2 ml-3">
                  <Button
                    variant="primary"
                    disabled={!selectedIds.length}
                    onClick={async () => {
                      try {
                        await removeFromBillingQueue({ orders: selectedIds }).unwrap()
                        setSelectedIds([])
                      } catch (e) {
                        if (e?.data?.message) {
                          confirm('', {
                            title: 'Error',
                            description: e?.data?.message,
                            yesAction: 'OK',
                            noAction: undefined,
                          })
                        }
                      }
                    }}
                    className="mr-3">
                    Remove from CureMD Queue
                  </Button>
                  <Button
                    variant="primary"
                    onClick={async () => {
                      try {
                        await confirm('', {
                          title: 'Are you sure?',
                          description: `This will ${tableHelpers.bucket === 'BILLING_QUEUED' ? 'submit' : 're-submit'} ${selectedIds.length ? `all selected (${selectedIds.length})` : `*ALL* ${tableHelpers.bucket === 'BILLING_QUEUED' ? 'pending' : 'failed'} (${orders?.items?.length})`} orders to CureMD.`,
                          yesAction: 'Submit',
                          noAction: 'Cancel',
                        })
                        await submitBilling({ orders: selectedIds }).unwrap()
                        setSelectedIds([])
                      } catch {}
                    }}>
                    {tableHelpers.bucket === 'BILLING_FAILED' ? 'Re-Submit' : 'Submit'} To CureMD
                  </Button>
                </div>
              )}
              {tableHelpers.bucket === 'FAILED_FAXES' && (
                <Button
                  variant="primary"
                  disabled={!selectedIds.length || isRetryingFaxes}
                  className="ml-2 mb-4"
                  onClick={() => {
                    // Rows already carry every report fax; FAILED_PERMANENTLY are the ones the
                    // Reports & Faxing panel's per-report Retry button targets.
                    const items = (orders as { items?: any[] } | undefined)?.items || []
                    const missingIds = selectedIds.filter((id) => !items.some((o) => Number(o.id) === id))
                    const retryTargets = selectedIds
                      .map((orderId) => ({
                        orderId,
                        reports: (items.find((o) => Number(o.id) === orderId)?.faxing || [])
                          .filter((f: { status: string }) => f.status === 'FAILED_PERMANENTLY')
                          .map((f: { reportId: number }) => String(f.reportId)),
                      }))
                      .filter((t) => t.reports.length)
                    const faxCount = retryTargets.reduce((acc, t) => acc + t.reports.length, 0)

                    if (!faxCount) {
                      const nothingDescription = 'The selected orders have no failed faxes to retry.' +
                        (missingIds.length > 0 ? ` ${missingIds.length} selected order${missingIds.length === 1 ? ' is' : 's are'} no longer on this page and will be skipped.` : '')
                      confirm('', {
                        title: 'Nothing to retry',
                        description: nothingDescription,
                        yesAction: 'OK',
                        noAction: undefined,
                      }).catch(() => {})
                      return
                    }

                    const retryDescription = `This will retry ${faxCount} failed fax${faxCount === 1 ? '' : 'es'} across ${retryTargets.length} order${retryTargets.length === 1 ? '' : 's'}.` +
                      (missingIds.length > 0 ? ` ${missingIds.length} selected order${missingIds.length === 1 ? ' is' : 's are'} no longer on this page and will be skipped.` : '')
                    confirm('', {
                      title: 'Retry failed faxes?',
                      description: retryDescription,
                      yesAction: 'Retry',
                      noAction: 'Cancel',
                    })
                      .then(async () => {
                        setIsRetryingFaxes(true)
                        const failedOrderIds: number[] = []
                        try {
                          // ponytail: fixed batches of 10; tune only if the API ever struggles
                          for (let i = 0; i < retryTargets.length; i += 10) {
                            const batch = retryTargets.slice(i, i + 10)
                            const results = await Promise.allSettled(
                              batch.map((t) =>
                                refaxOrderReport({ orderId: t.orderId, reports: t.reports }).unwrap(),
                              ),
                            )
                            results.forEach((r, idx) => {
                              if (r.status === 'rejected') failedOrderIds.push(batch[idx].orderId)
                            })
                          }
                        } finally {
                          setIsRetryingFaxes(false)
                        }
                        setSelectedIds([])
                        refetchOrders()
                        if (failedOrderIds.length) {
                          confirm('', {
                            title: 'Some retries failed',
                            description: `Retry failed for order${failedOrderIds.length === 1 ? '' : 's'} ${failedOrderIds.join(', ')}. Open them to retry individually.`,
                            yesAction: 'OK',
                            noAction: undefined,
                          }).catch(() => {})
                        }
                      })
                      .catch(() => {})
                  }}>
                  {isRetryingFaxes ? 'Retrying…' : 'Retry Selected Faxes'}
                </Button>
              )}
              <DataTable
                data={orders}
                getRowCss={getAuthorizationExpirationRowCss}
                tableKey={`lab_orders_${tableHelpers.testType}_${tableHelpers.bucket}`}
                status={isLoading}
                tableColumns={tableColumns}
                select={bucketColumnDefaults[tableHelpers.testType === 'HST' ? 'HST' : 'POXCOX']?.[
                  tableHelpers.bucket
                ]?.some((c) => c === 'select')}
                pageSize={tableHelpers.pageSize}
                setCurrentPage={tableHelpers.setCurrentPage}
                setSortOptions={tableHelpers.setSortOptions}
              />
              <MarkBilledDialog
                show={showMarkBilledDialog}
                onClose={() => setShowMarkBilledDialog(false)}
                title="Mark Orders as Billed?"
                description={`Are you sure you want to mark ${selectedIds.length} selected order${selectedIds.length === 1 ? '' : 's'} as billed?`}
                successLabel={`${selectedIds.length} order${selectedIds.length === 1 ? '' : 's'} marked as billed.`}
                markOrderAsBilled={async (reason: string) => {
                  const ordersToMark = [...selectedIds]
                  await markOrdersAsBilled({ orders: ordersToMark, reason }).unwrap()
                  setSelectedIds([])
                  await refetchOrders()
                }}
              />
            </StyledTableWrapper>
          </div>
        }
        breadcrumb={[{ label: 'Home', to: `${routes.index}` }]}
        subtitle={''}
      />
    </div>
  )
}

export default Order
